import {ChangeDetectorRef, Component, OnInit, Optional} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {LayoutService} from '@ngmedax/layout';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {flagIconCodeMap, languages} from '@ngmedax/cloud-translator';

import {ThiemeCatalogEntry, ThiemeCatalogFilter, ThiemeCatalogIncompatibleRemote, ThiemeCatalogStatus} from '../../../../types';
import {TRANSLATION_GRID_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ThiemeCatalogService} from '../../services/thieme-catalog.service';
import {ThiemeImportService} from '../../services/thieme-import.service';
import {ApiService} from '../../services/api.service';


// hack to inject decorator declarations. must occur before class declaration!
export interface ThiemeCatalogGridComponent extends Translatable {}

@Component({
  selector: 'app-thieme-catalog-grid',
  templateUrl: './thieme-catalog-grid.component.html',
  styleUrls: ['./thieme-catalog-grid.component.css']
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class ThiemeCatalogGridComponent implements OnInit {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public entries: ThiemeCatalogEntry[] = [];
  public total = 0;
  public catalogVersion: string = null;

  /**
   * Newer central delivery the instance refused because it requires an
   * unsupported questionnaire schema version (shown as update hint banner)
   */
  public incompatibleRemote: ThiemeCatalogIncompatibleRemote = null;
  public filter: ThiemeCatalogFilter = {
    sort: 'bogencode',
    order: 'asc'
  };

  /**
   * Select options for the filter dropdowns, built once from the full catalog
   */
  public specialties: string[] = [];
  public bodyRegions: string[] = [];
  public formTypes: string[] = [];
  public languageCodes: string[] = [];

  /**
   * Locale code to flag icon code mapping (flag-icon-css)
   */
  public flagIconCodeMap = flagIconCodeMap;

  /**
   * Map of thieme uuid to locally imported editorial version
   */
  public localVersions: {[uuid: string]: string} = {};

  /**
   * Set of uuids known locally (needed because editorial version may be null)
   */
  public localUuids: {[uuid: string]: boolean} = {};

  /**
   * Per row busy state while downloading/importing
   */
  public busy: {[uuid: string]: boolean} = {};

  /**
   * Timeout for filter change event
   */
  private filterChangeTimeout: any = null;

  /**
   * Timeout for show preloader event
   */
  private showPreloaderTimeout: any = null;

  public constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    private catalogService: ThiemeCatalogService,
    private importService: ThiemeImportService,
    private apiService: ApiService,
    private ref: ChangeDetectorRef,
    @Optional() private route: ActivatedRoute
  ) {
  }

  /**
   * Loads catalog, local status map and filter options.
   * A "fulltext" query param prefills the search filter (deep link from the
   * origin badge in other grids).
   */
  public ngOnInit() {
    const fulltext = this.route && this.route.snapshot ? this.route.snapshot.queryParams['fulltext'] : null;
    fulltext && (this.filter.fulltext = `${fulltext}`);

    this.layoutService.showPreloader();
    this.loadLocalVersions().then(() => this.loadCatalog());
    this.loadFilterOptions();
  }

  /**
   * Refreshes the grid data
   */
  public async onRefresh() {
    this.showPreloader();
    await this.loadLocalVersions();
    this.loadCatalog();
  }

  /**
   * Event handler for when paging changes
   */
  public onPagingChange() {
    this.ref.detectChanges();
    this.ref.markForCheck();
    this.loadCatalog();
  }

  /**
   * Event handler for when a filter changes (debounced for text input)
   */
  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => {
      this.gridPageNumber = 1;
      this.loadCatalog();
    }, 500);
  }

  /**
   * Changes sort order on column click
   * @param {string} col
   */
  public onSortChange(col: string) {
    if (this.filter.sort === col) {
      this.filter.order = this.filter.order === 'asc' ? 'desc' : 'asc';
      this.loadCatalog();
      return;
    }
    this.filter.sort = col;
    this.filter.order = 'asc';
    this.loadCatalog();
  }

  /**
   * Resets the search form
   */
  public onResetSearchForm() {
    this.filter.fulltext = '';
    this.filter.specialty = '';
    this.filter.bodyRegion = '';
    this.filter.formType = '';
    this.filter.language = '';
    this.filter.downloadStatus = '';
    this.onFilterChange();
  }

  /**
   * Returns the download status of the given entry compared to the local instance
   *
   * @param {ThiemeCatalogEntry} entry
   * @returns {ThiemeCatalogStatus}
   */
  public getStatus(entry: ThiemeCatalogEntry): ThiemeCatalogStatus {
    if (!this.localUuids[entry.uuid]) {
      return 'not-downloaded';
    }

    // plain equality on the editorial version string - null == null counts as up to date
    const local = this.localVersions[entry.uuid] || null;
    const remote = entry.editorialVersion || null;
    return local === remote ? 'up-to-date' : 'update-available';
  }

  /**
   * Returns the translated status tooltip of the given entry
   *
   * @param {ThiemeCatalogEntry} entry
   * @returns {string}
   */
  public getStatusTooltip(entry: ThiemeCatalogEntry): string {
    const status = this.getStatus(entry);

    if (status === 'not-downloaded') {
      return this._(KEYS.GRID.STATUS_NOT_DOWNLOADED);
    }

    if (status === 'up-to-date') {
      return this._(KEYS.GRID.STATUS_UP_TO_DATE);
    }

    return this._(KEYS.GRID.STATUS_UPDATE_AVAILABLE, {version: entry.editorialVersion || '-'});
  }

  /**
   * Returns the translated language name for the given locale code
   * (e.g. de_DE -> Deutsch / German). Falls back to the code itself.
   *
   * @param {string} code locale code
   * @returns {string}
   */
  public translateLanguage(code: string): string {
    const name = languages[code] || code;

    return this.translationService ?
      this.translationService.translate({
        text: name,
        scope: ['cloudTranslatorModule', 'default']
      }) : name;
  }

  /**
   * Downloads and imports the given catalog entry
   *
   * @param {ThiemeCatalogEntry} entry
   */
  public async onDownload(entry: ThiemeCatalogEntry) {
    if (this.busy[entry.uuid]) {
      return;
    }

    this.busy[entry.uuid] = true;

    try {
      const blob = await this.catalogService.downloadMqt(entry.sapId);
      await this.importService.import(blob, entry.file);

      // reflect the new local state without a full reload
      this.localUuids[entry.uuid] = true;
      this.localVersions[entry.uuid] = entry.editorialVersion || null;

      alert(this._(KEYS.GRID.SUCCESSFULLY_IMPORTED));
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.GRID.ERROR_IMPORTING));
    }

    this.busy[entry.uuid] = false;
  }

  /**
   * Loads the catalog page for the current filter state (server side)
   */
  private loadCatalog() {
    this.showPreloader();

    this.catalogService.getCatalog(this.getQueryFilter(), this.getQueryOpts())
      .then((result) => {
        this.entries = result.items;
        this.total = result.total;
        this.catalogVersion = result.version;
        this.incompatibleRemote = result.incompatibleRemote;
        this.hidePreloader();
      })
      .catch((error) => {
        console.error(error);
        this.hidePreloader();
        const status = error && error.status ? error.status : 0;
        alert(this._(status === 403 || status === 401 ? KEYS.GRID.ERROR_LICENSE : KEYS.GRID.ERROR_LOADING_CATALOG));
      });
  }

  /**
   * Loads the thieme questionnaires of the instance and builds the
   * uuid to editorial version map for the status column
   */
  private async loadLocalVersions(): Promise<void> {
    try {
      const result = await this.apiService.loadQuestionnaires({'meta.source.provider': 'thieme'}, {limit: 0});
      const localVersions: {[uuid: string]: string} = {};
      const localUuids: {[uuid: string]: boolean} = {};

      for (const questionnaire of result.rows || []) {
        const uuid = questionnaire.id || questionnaire['_id'];
        const meta: any = questionnaire.meta || {};
        localUuids[uuid] = true;
        localVersions[uuid] = meta.source && meta.source.editorialVersion ? meta.source.editorialVersion : null;
      }

      this.localVersions = localVersions;
      this.localUuids = localUuids;
    } catch (error) {
      // status column degrades gracefully when the questionnaire api is not reachable
      console.error(error);
    }
  }

  /**
   * Builds the filter dropdown options from the full catalog (one time load).
   * Filtering itself always runs server side.
   */
  private loadFilterOptions() {
    this.catalogService.getCatalog({}, {limit: 10000})
      .then((result) => {
        const collect = (field: string): string[] => {
          const seen: {[value: string]: boolean} = {};
          for (const entry of result.items) {
            entry[field] && (seen[entry[field]] = true);
          }
          return Object.keys(seen).sort();
        };

        this.specialties = collect('specialty');
        this.bodyRegions = collect('bodyRegion');
        this.formTypes = collect('formType');
        this.languageCodes = collect('language')
          .sort((a, b) => this.translateLanguage(a).localeCompare(this.translateLanguage(b)));
      })
      .catch((error) => console.error(error));
  }

  /**
   * Returns the mongo query for the current filter state
   */
  private getQueryFilter(): any {
    const filter: any = {};

    if (this.filter.fulltext) {
      filter.$or = [
        {bogencode: {'$regex': this.filter.fulltext, '$options': 'i'}},
        {shortTitle: {'$regex': this.filter.fulltext, '$options': 'i'}}
      ];
    }

    this.filter.specialty && (filter.specialty = this.filter.specialty);
    this.filter.bodyRegion && (filter.bodyRegion = this.filter.bodyRegion);
    this.filter.formType && (filter.formType = this.filter.formType);
    this.filter.language && (filter.language = this.filter.language);

    // download status filter: match against the uuids of locally imported thieme questionnaires
    if (this.filter.downloadStatus === 'downloaded') {
      filter.uuid = {'$in': Object.keys(this.localUuids)};
    }

    if (this.filter.downloadStatus === 'not-downloaded') {
      filter.uuid = {'$nin': Object.keys(this.localUuids)};
    }

    return filter;
  }

  /**
   * Returns query opts for pagination and sorting
   */
  private getQueryOpts(): {limit: number, offset: number, sort: string} {
    const queryOpts = {
      limit: this.displayPerPage,
      offset: (this.gridPageNumber - 1) * this.displayPerPage,
      sort: ''
    };

    if (!this.filter.sort) {
      return queryOpts;
    }

    queryOpts.sort = this.filter.order === 'asc' ? this.filter.sort : '-' + this.filter.sort;
    return queryOpts;
  }

  /**
   * Shows preloader after 200ms
   */
  private showPreloader() {
    this.showPreloaderTimeout = setTimeout(() => this.layoutService.showPreloader(), 200);
  }

  /**
   * Hides preloader
   */
  private hidePreloader() {
    clearTimeout(this.showPreloaderTimeout);
    this.layoutService.hidePreloader();
    setTimeout(() => this.layoutService.hidePreloader(), 1000);
  }
}
